import { core, formatDay, schedule, type Day, type Decimal, type GoalProgress } from '@map-my-money/engine';
import { definitionsFor } from './definitions.ts';
import { money, percentChange } from './money.ts';
import { lastAmount, runForecast, type Forecasted, type WaypointInput, type WirePlan } from './run.ts';

/** Calendar months from `from`'s month to `to`'s: goal dates are month ends (or today), so whole months by day would undercount. */
export const calendarMonths = (from: Day, to: Day): number => {
  const a = core.toYmd(from);
  const b = core.toYmd(to);
  return (b.year - a.year) * 12 + (b.month - a.month);
};

/**
 * A finer measure than whole months, for ranking changes (the lever sweep): how much further toward
 * its target the goal's series is with the change, at one month end: the goal's "by" month, else the
 * month it was due to be reached, else the end of the comparison. Positive is closer. Null when that
 * month is before the line (a by month already past) or beyond it.
 */
function gainAt(goal: GoalProgress, before: Forecasted, after: Forecasted): { on: Day; amount: Decimal } | null {
  const series = goal.goal.series;
  const lineBefore = before.forecast.cumulative[series];
  const lineAfter = after.forecast.cumulative[series];
  const target = goal.deadline?.date ?? (goal.isAchieved ? before.today : goal.estimatedDate) ?? core.addDays(lineBefore[lineBefore.length - 1]?.end ?? before.today, -1);
  const index = lineBefore.findIndex((bucket) => calendarMonths(core.addDays(bucket.end, -1), target) === 0);
  if (index < 0 || lineAfter[index] === undefined) return null;
  const difference = core.sub(lineAfter[index]!.amount, lineBefore[index]!.amount);
  return { on: core.addDays(lineBefore[index]!.end, -1), amount: goal.goal.operation === 'crossunder' ? core.negate(difference) : difference };
}

/** The forecast before vs after a change of plan or of waypoints (a new waypoint re-anchors it), at the after plan's horizon. */
export function compare(before: WirePlan, after: WirePlan, waypointsBefore: readonly WaypointInput[], waypointsAfter: readonly WaypointInput[], asOf: string) {
  const years = after.configuration.timeframeYears;
  return compareForecasts(runForecast({ plan: before, waypoints: waypointsBefore, asOf }, years), runForecast({ plan: after, waypoints: waypointsAfter, asOf }, years));
}

/** `compare` on two forecasts already run (same day and horizon), for a screen that also draws their lines. */
export function compareForecasts(a: Forecasted, b: Forecasted) {
  const years = b.years;
  // Year 0 is now (R20); year y the month end the samples always took.
  const fromNow = (f: typeof a) => schedule.yearlySamples(f.forecast.cumulative.netWorth, years).map((s) => (s.year === 0 ? { ...s, value: f.forecast.now.netWorth } : s));
  const beforeYearly = fromNow(a);
  const afterYearly = fromNow(b);
  const finalBefore = lastAmount(a.forecast.cumulative.netWorth);
  const finalAfter = lastAmount(b.forecast.cumulative.netWorth);
  const afterGoals = new Map(b.goals.map((g) => [g.goal.id, g]));
  const goals = a.goals.flatMap((g) => {
    const other = afterGoals.get(g.goal.id);
    if (!other) return [];
    const was = g.isAchieved ? a.today : g.estimatedDate;
    const now = other.isAchieved ? b.today : other.estimatedDate;
    const monthsSooner = was !== null && now !== null ? calendarMonths(now, was) : null;
    const gain = gainAt(g, a, b);
    return [
      {
        id: g.goal.id,
        label: g.goal.label,
        before: was === null ? null : formatDay(was),
        after: now === null ? null : formatDay(now),
        months_sooner: monthsSooner,
        becomes_reachable: was === null && now !== null,
        becomes_unreachable: was !== null && now === null,
        measured_on: gain === null ? null : formatDay(gain.on),
        gain: gain === null ? null : money(gain.amount),
        ...(g.deadline && other.deadline ? { shortfall_before: money(g.deadline.shortfall), shortfall_after: money(other.deadline.shortfall) } : {}),
      },
    ];
  });
  const change = core.sub(finalAfter, finalBefore);
  return {
    years,
    final_net_worth_before: money(finalBefore),
    final_net_worth_after: money(finalAfter),
    net_worth_change: money(change),
    net_worth_change_percent: percentChange(finalBefore, finalAfter),
    yearly: beforeYearly.map((s, i) => ({
      year: s.year,
      before: money(s.value),
      after: money(afterYearly[i]?.value ?? s.value),
    })),
    goals,
    goals_moved: goals.filter((g) => (g.months_sooner !== null && g.months_sooner !== 0) || g.becomes_reachable || g.becomes_unreachable),
  };
}

export type Comparison = ReturnType<typeof compare>;

export const comparePlans = (before: WirePlan, after: WirePlan, waypoints: readonly WaypointInput[], asOf: string): Comparison =>
  compare(before, after, waypoints, waypoints, asOf);

/** A what-if: the plan as it is vs with the changes, never saved. `changes` are the human-readable lines of the change set. */
export function whatIfView(before: WirePlan, after: WirePlan, waypoints: readonly WaypointInput[], asOf: string, changes: readonly string[]) {
  return {
    view: 'what_if' as const,
    as_of: asOf,
    changes: [...changes],
    comparison: comparePlans(before, after, waypoints, asOf),
    definitions: definitionsFor('projection', 'goal_progress'),
  };
}

export type WhatIfViewResult = ReturnType<typeof whatIfView>;
